import React, { useEffect, useRef, useState } from "react";
import FormUI from '@/componentsUI/Form';
import { feedback } from "@/utils/feedback";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import {
  useWalletAll,
  useWalletWithdrawFlat,
  useWithdrawFlat,
} from "@/store/walletStore";
import { useUserInfo } from "@/store/userInfo";
import FromItemPlus from "@/components/FormItemPlus/FromItemPlus";
import WidthdrawCurrency from "../compoments/widthdrawCurrency/WidthdrawCurrency";
import LoadingUI from "@/componentsUI/LoadingUI/LoadingUI";
import { IFInitWalletWithdrawFlatReq } from "@/api/apiReq/IFWalletReq";
import { sys } from "@/Core/utils/system";
import WidthdrawPix from "../pix/WidthdrawPix";
import {
  IFInitWalletRsp,
  IFInitWalletWithdrawFlatRsp,
} from "@/api/apiRsp/IFWalletRsp";
import ButtonUI from "@/componentsUI/Button";
import "../index.less";
import useDialogManager from "@/utils/useDialogManager";
import {
  AmountMultiplierConversion,
  dealAmount9Valid,
} from "@/Core/utils/currencyDealCenter";
import useDialog from "@/utils/useDialog";
import InternalWithdrawWayField from "../compoments/widthdrawWay/InternalWithdrawWayField";
import WidthdrawedPix from "../pix/WidthdrawedPix";
import BankSelector from "../compoments/bankSelector/BankSelector";
import BankAccountInput from "../compoments/bankAccountInput/BankAccountInput";
import MobileInput from "../compoments/mobileInput/MobileInput";
import { getWithdrawalMobile } from "@/utils/mobileUtils";
import NigeriaBankCode102 from "../assets/ng/Bankcode-102.json";
import {
  getWithdrawBankInfo,
  saveWithdrawBankInfo,
} from "../withdrawStorage";
import { runWithdrawalOfficialAppPrompt } from "@/features/install/withdrawalOfficialAppPrompt";

const FlatWithdrawNigeria = () => {
  const [form] = FormUI.useForm();
  const { t } = useTranslationPlus();
  const { data: wallets } = useWalletAll();
  const { data: userInfo } = useUserInfo();
  const formValidateRef = useRef(null);
  const [params, setParams] = useState<IFInitWalletWithdrawFlatReq>(
    wallets?.flat
      ? {
          currencyType:
            wallets?.flat[0]?.serverCurrencyType ||
            wallets?.flat[0]?.currencyType,
          device: sys.device,
        }
      : null,
  );
  const { data, isValidating, error, mutate } = useWithdrawFlat(params);
  const [currentWallet, setCurrentWallet] = useState<IFInitWalletRsp>(
    wallets?.flat?.[0],
  );

  const [widthdrawConfig, setWidthdrawConfig] =
    useState<IFInitWalletWithdrawFlatRsp>();

  const [selectedBank, setSelectedBank] = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [availableChannels, setAvailableChannels] = useState<number[]>([]);
  const [nigeriaBankCode104, setNigeriaBankCode104] = useState<any[] | null>(
    null,
  );
  const [selectedWithdrawType, setSelectedWithdrawType] = useState<number>(102);

  useEffect(() => {
    if (availableChannels.length === 0) {
      return;
    }

    if (!availableChannels.includes(selectedWithdrawType)) {
      setSelectedBank(null);
      setAccountNumber("");
      form.setFieldValue("bankCode", undefined);
      form.setFieldValue("accountNumber", "");
      return;
    }

    const bankInfo = getWithdrawBankInfo(userInfo?.userId);
    if (
      bankInfo?.bank &&
      bankInfo.accountNumber &&
      bankInfo.withdrawType === selectedWithdrawType
    ) {
      setSelectedBank(bankInfo.bank);
      setAccountNumber(bankInfo.accountNumber);
      form.setFieldValue("bankCode", bankInfo.bank.code);
      form.setFieldValue("accountNumber", bankInfo.accountNumber);
    } else {
      setSelectedBank(null);
      setAccountNumber("");
      form.setFieldValue("bankCode", undefined);
      form.setFieldValue("accountNumber", "");
    }
  }, [form, selectedWithdrawType, availableChannels, userInfo?.userId]);

  useEffect(() => {
    if (
      nigeriaBankCode104 ||
      selectedWithdrawType !== 104 ||
      !availableChannels.includes(104)
    ) {
      return;
    }
    let isMounted = true;
    import(
      /* webpackChunkName: "bankcode-104-ng" */
      "../assets/ng/Bankcode-104.json"
    )
      .then((mod) => {
        if (isMounted) {
          setNigeriaBankCode104(mod.default || mod);
        }
      })
      .catch((error) => {
        console.error("Failed to load Nigeria Bankcode-104:", error);
      });
    return () => {
      isMounted = false;
    };
  }, [availableChannels, nigeriaBankCode104, selectedWithdrawType]);

  function changeWidthdraw(index) {
    const item = wallets.flat?.[index];
    if (!item) {
      return;
    }
    setParams({
      currencyType: item.serverCurrencyType || item.currencyType,
      device: sys.device,
    });
    setCurrentWallet(item);
  }

  useEffect(() => {
    if (wallets?.flat?.length) {
      form.setFieldValue("walletType", wallets?.flat?.[0]?.currencyType);
    }
  }, [wallets]);

  useEffect(() => {
    form.setFieldValue(
      "mobile",
      getWithdrawalMobile(userInfo?.mobile, userInfo?.userId)
    );
  }, [userInfo]);

  const { trigger } = useWalletWithdrawFlat();
  const { openGoogleTowFactor } = useDialogManager();
  const { closeAll } = useDialog();

  const pixWidthdraw = (v) => {
    if (!selectedBank || !accountNumber) {
      feedback.error(t("Wallet.Please select bank and enter account number"));
      return;
    }

    const cpf = v?.cpf?.replaceAll(".", "").replaceAll("-", "");
    const selectedWallet = wallets?.flat?.find(
      (wallet) => wallet.currencyType === v.walletType,
    );
    const requestParams = {
      currencyType: selectedWallet?.serverCurrencyType || v.walletType,
      money: AmountMultiplierConversion(Math.floor(v.amount), {
        currencyType: selectedWallet?.currencyType,
        serverCurrencyType: selectedWallet?.serverCurrencyType,
      }),
      withdrawType: selectedWithdrawType,
      accountInfo: JSON.stringify({
        pixFullName: v.accountName,
        pixCpf: cpf,
        pixType: selectedBank.code,
        pixAccount: cpf,
        bankCode: selectedBank.code,
        bankAccount: accountNumber,
      }),
      accountType: selectedBank.code,
      accountNumber: accountNumber,
      mobile: v.mobile || "",
      withdrawMobileUserId: userInfo?.userId,
    };
    trigger(requestParams)
      .then((res) => {
        if (res?.googleAuth) {
          openGoogleTowFactor({
            trigger: trigger,
            params: requestParams,
            complete: () => {
              const bankInfo = {
                bank: selectedBank,
                accountNumber: accountNumber,
                withdrawType: selectedWithdrawType,
              };
              saveWithdrawBankInfo(bankInfo, userInfo?.userId);
              closeAll();
            },
          });
        } else {
          const bankInfo = {
            bank: selectedBank,
            accountNumber: accountNumber,
            withdrawType: selectedWithdrawType,
          };
          saveWithdrawBankInfo(bankInfo, userInfo?.userId);
          closeAll();
        }
      })
      .catch((e) => {
        if (e?.code == "ERROR_60028") {
          if (e?.data?.withdrawAmount == 0) {
            feedback.error(
              t("WITHDRAW_NEED_BET", {
                betCode: dealAmount9Valid(e?.data?.betTarget, {
                  currencyType: e?.data?.currency,
                }),
              }),
            );
          } else {
            feedback.error(
              t("WITHDRAW_ALREADY_BET", {
                alreadyCode: dealAmount9Valid(e?.data?.withdrawAmount, {
                  currencyType: e?.data?.currency,
                }),
              }),
            );
          }
        } else {
          feedback.error(t(e?.code));
        }
      });
  };

  const onClickWidthdraw = () => {
    if (!selectedBank) {
      feedback.error(t("Wallet.Please select a bank"));
      return;
    }

    formValidateRef.current = true;
    form
      .validate()
      .then((v) =>
        runWithdrawalOfficialAppPrompt({
          stage: "before_submit",
          userId: userInfo?.userId,
          onContinue: () => pixWidthdraw(v),
        }),
      )
      .finally(() => {
        formValidateRef.current = false;
      });
  };

  function changeType({ data }) {
    setWidthdrawConfig(data);
  }

  useEffect(() => {
    if (data && data?.configParams) {
      const channels = data.configParams.map((config) => config.withdrawType);
      setAvailableChannels(channels);

      let savedTypeNum: number | null = null;
      const bankInfo = getWithdrawBankInfo(userInfo?.userId);
      if (bankInfo) {
        savedTypeNum = bankInfo.withdrawType;
      }

      let initialType: number;
      if (savedTypeNum && channels.includes(savedTypeNum)) {
        initialType = savedTypeNum;
      } else {
        initialType = data.configParams[0].withdrawType;
      }

      setSelectedWithdrawType(initialType);

      const initialConfig =
        data.configParams.find(
          (config) => config.withdrawType === initialType,
        ) || data.configParams[0];

      form.setFieldValue("witdrawWay", {
        type: initialConfig.withdrawType,
        data: initialConfig,
      });
      setWidthdrawConfig(initialConfig);
    }
  }, [data, userInfo?.userId]);

  const handleBankSelect = (bank) => {
    setSelectedBank(bank);
    if (bank) {
      form.setFieldValue("bankCode", bank.code);
    }
  };

  const handleAccountNumberChange = (value) => {
    setAccountNumber(value);
    form.setFieldValue("accountNumber", value);
  };

  const handleWithdrawTypeChange = (type: number) => {
    setSelectedWithdrawType(type);
    setSelectedBank(null);
    setAccountNumber("");
    form.setFieldValue("bankCode", undefined);
    form.setFieldValue("accountNumber", "");

    if (!availableChannels.includes(type)) {
      return;
    }

    const newConfig = data?.configParams?.find(
      (config) => config.withdrawType === type,
    );
    if (newConfig) {
      setWidthdrawConfig(newConfig);
      form.setFieldValue("witdrawWay", {
        type: newConfig.withdrawType,
        data: newConfig,
      });
    }

    const bankInfo = getWithdrawBankInfo(userInfo?.userId);
    if (
      bankInfo?.withdrawType === type &&
      bankInfo.bank &&
      bankInfo.accountNumber
    ) {
      setSelectedBank(bankInfo.bank);
      setAccountNumber(bankInfo.accountNumber);
      form.setFieldValue("bankCode", bankInfo.bank.code);
      form.setFieldValue("accountNumber", bankInfo.accountNumber);
    }
  };

  return (
    <div className="flat-widthdraw">
      <FormUI form={form} layout={"vertical"} autoComplete={"off"}>
        <FromItemPlus
          field={"walletType"}
          label={t("Wallet.WithdrawCurrencyTitle")}
        >
          <WidthdrawCurrency
            currencys={wallets?.flat}
            onChange={(_value: any, option: any) => {
              changeWidthdraw(option?._index);
            }}
          />
        </FromItemPlus>

        <LoadingUI
          isLoading={isValidating}
          error={error}
          dataList={data}
          retryFunc={mutate}
          className="wallet-flat-loading-state"
        >
          <>
            <InternalWithdrawWayField
              configs={data?.configParams}
              onChange={changeType}
            />

            <div className="withdraw-bank-section">
              <div className="bank-selector-wrapper">
                <label className="bank-selector-label">
                  <span className="withdraw-required-mark">*</span>
                  {t("Wallet.Select Bank")}
                </label>
                <BankSelector
                  value={selectedBank}
                  onChange={handleBankSelect}
                  placeholder={t("Wallet.Select Bank")}
                  withdrawType={selectedWithdrawType}
                  onWithdrawTypeChange={handleWithdrawTypeChange}
                  availableChannels={availableChannels}
                  bankCodeDataByChannel={{
                    102: NigeriaBankCode102,
                    104: nigeriaBankCode104 || [],
                  }}
                />
              </div>

              <FromItemPlus
                field={"accountNumber"}
                label={t("Wallet.Account Number")}
                rules={[
                  {
                    required: true,
                    message: t("Wallet.Please enter account number"),
                  },
                  {
                    min: 10,
                    message: t(
                      "Wallet.Account number must be at least 10 digits",
                    ),
                  },
                ]}
              >
                <BankAccountInput
                  value={accountNumber}
                  onChange={handleAccountNumberChange}
                  placeholder={t(
                    "Wallet.Please enter the correct account number",
                  )}
                />
              </FromItemPlus>
            </div>

            <div className="withdraw-mobile-section">
              <FromItemPlus
                field={"mobile"}
                label={t("Wallet.Mobile Number")}
                className="withdraw-mobile-item"
                rules={[
                  {
                    required: true,
                    message: t("Wallet.Please enter mobile number"),
                  },
                  {
                    validator: (value, cb) => {
                      if (value && (value.length < 8 || value.length > 10)) {
                        return cb(t("Wallet.Mobile number must be"));
                      }
                      return cb();
                    },
                  },
                ]}
              >
                <MobileInput disabled={true} />
              </FromItemPlus>
              <div className="important-notice">
                {t("Wallet.WithdrawPhoneNotice")}
              </div>
            </div>

            {selectedWithdrawType === 102 && (
              <WidthdrawPix
                data={data}
                widthdrawConfig={widthdrawConfig}
                currentWallet={currentWallet}
                form={form}
                formValidateRef={formValidateRef}
              />
            )}
            {selectedWithdrawType === 104 && !data?.userAccount?.pixCpf && (
              <WidthdrawPix
                data={data}
                widthdrawConfig={widthdrawConfig}
                currentWallet={currentWallet}
                form={form}
                formValidateRef={formValidateRef}
              />
            )}
            {selectedWithdrawType === 104 && data?.userAccount?.pixCpf && (
              <WidthdrawedPix
                data={data}
                widthdrawConfig={widthdrawConfig}
                currentWallet={currentWallet}
                form={form}
                formValidateRef={formValidateRef}
              />
            )}
          </>
        </LoadingUI>
      </FormUI>

      <ButtonUI
        className={`widthdraw-btn`}
        type="primary"
        onClick={onClickWidthdraw}
      >
        {t("Wallet.Withdraw")}
      </ButtonUI>
    </div>
  );
};

export default FlatWithdrawNigeria;
